// "Saved views" header control — save the current FilterGroup under a name,
// then re-apply or delete saved ones. Replaces the old disabled stub. Views are
// per-user (backend scopes them by Cognito sub).
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSavedView,
  deleteSavedView,
  listSavedViews,
  type SavedView,
} from "../../lib/api";
import { useExplorerStore } from "../../store/explorerStore";
import { migrateLegacyMultipicklistOperators } from "../../lib/filterLogic";

const QK = ["saved-views"] as const;
const CURRENT_FILTER_SCHEMA_VERSION = 3;

export function SavedViewsControl() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const filterGroup = useExplorerStore((s) => s.filterGroup);
  const setFilterGroup = useExplorerStore((s) => s.setFilterGroup);
  const qc = useQueryClient();

  const { data: views = [], isLoading, isError: isListError } = useQuery({
    queryKey: QK,
    queryFn: listSavedViews,
    enabled: open, // only fetch once the menu is opened
  });

  const saveMut = useMutation({
    mutationFn: () => createSavedView(name.trim(), filterGroup),
    onSuccess: () => {
      setName("");
      void qc.invalidateQueries({ queryKey: QK });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSavedView(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QK }),
  });

  const apply = (v: SavedView) => {
    if (v.error) return;
    setFilterGroup(
      (v.filter_schema_version ?? 1) < CURRENT_FILTER_SCHEMA_VERSION
        ? migrateLegacyMultipicklistOperators(v.filter_group)
        : v.filter_group,
    );
    setOpen(false);
  };

  const canSave = name.trim().length > 0 && !saveMut.isPending;

  return (
    <div className="saved-views">
      <button
        type="button"
        className="saved-views-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="star">★</span>
        <span>Saved views</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="saved-views__menu" role="menu">
          <form
            className="saved-views__save"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSave) saveMut.mutate();
            }}
          >
            <input
              type="text"
              maxLength={120}
              placeholder="Save current filters as…"
              aria-label="New saved view name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <button type="submit" disabled={!canSave}>Save</button>
          </form>
          {saveMut.isError && (
            <p className="saved-views__error" role="alert">Couldn't save the view.</p>
          )}

          {deleteMut.isError && (
            <p className="saved-views__error" role="alert">Couldn't delete the view.</p>
          )}

          <div className="saved-views__list">
            {isLoading && <p className="saved-views__muted">Loading…</p>}
            {isListError && (
              <p className="saved-views__error" role="alert">Couldn't load saved views.</p>
            )}
            {!isLoading && !isListError && views.length === 0 && (
              <p className="saved-views__muted">No saved views yet.</p>
            )}
            {views.map((v) => (
              <div className="saved-views__item" key={v.id}>
                <button
                  type="button"
                  className="saved-views__apply"
                  role="menuitem"
                  disabled={v.error !== null}
                  onClick={() => apply(v)}
                  title={v.error?.message ?? "Apply this view"}
                >
                  {v.name}
                </button>
                <button
                  type="button"
                  className="saved-views__delete"
                  aria-label={`Delete ${v.name}`}
                  disabled={deleteMut.isPending}
                  onClick={() => deleteMut.mutate(v.id)}
                >
                  ×
                </button>
                {v.error && (
                  <p className="saved-views__error" role="status">
                    {v.error.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
